import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { Box } from "@mui/material";

const SpecialEdDialog = ({ open, onClose, reset }) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState([]);
    const { control } = useForm();

    const steps = [
        {
            value: "qualifies-special-ed",
            label: t("To qualify, one of the following must be true"),
            items: [
                t("My child has a current Individualized Education Program (IEP)."),
                t("My child has a current 504 Accommodation Plan."),
                t("My child is currently receiving special education services in their public school."),
            ],
        },
        {
            value: "proof-of-special-ed",
            label: t("Able to provide proof of one of the following"),
            items: [
                t("A copy of the IEP (current)."),
                t("A copy of the 504 plan."),
                t("A school notice or report card showing special education or related services."),
            ],
        },
    ];

    const handleConfirm = () => {
        if (selected.length > 0) {
            onClose();
        }
    };

    const handleCancel = () => {
        setSelected([]);
        reset("isSWD");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={() => {}}
            disableEscapeKeyDown
            maxWidth="sm"
            TransitionProps={{ onEntering: () => setSelected([]) }}
        >
            <DialogTitle>{t("Weighted Lottery for Students with Disabilities (SWDs)")}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" gutterBottom>
                    {t(
                        "Bronx ASCS uses a weighted lottery to ensure Students with Disabilities (SWDs) have a fair chance."
                    )}
                    {" "}
                    {t(
                        "If your child qualifies, they will receive two tickets in the lottery instead of one."
                    )} 
                    {t(
                        "This does not reserve or guarantee a seat."
                    )} 
                </Typography>

                <Controller
                    name="specialEdOptions"
                    control={control}
                    defaultValue={[]}
                    rules={{
                        validate: (value) =>
                            value?.length > 0 || t("Please select at least one option."),
                    }}
                    render={({ field, fieldState }) => (
                        <FormGroup>
                            {steps.map((step) => (
                                <Box key={step.value} sx={{ mb: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.value?.includes(step.value) || false}
                                                onChange={(e) => {
                                                    let newSelected;
                                                    if (e.target.checked) {
                                                        newSelected = [...(field.value || []), step.value];
                                                    } else {
                                                        newSelected = (field.value || []).filter((v) => v !== step.value);
                                                    }
                                                    field.onChange(newSelected);
                                                    setSelected(newSelected);
                                                }}
                                            />
                                        }
                                        label={<Typography variant="body1" fontWeight={500}>{step.label}</Typography>}
                                    />
                                    <Box sx={{ pl: 4, mt: 0 }}>
                                        {step.items.map((item, idx) => (
                                            <Typography key={idx} variant="body2" >
                                                • {item}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                            {fieldState.error && (
                                <p style={{ color: "red", fontSize: "0.875rem" }}>
                                    {fieldState.error.message}
                                </p>
                            )}
                        </FormGroup>
                    )}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>{t("Child Not Eligible")}</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={selected.length < 2}
                >
                    {t("Confirm")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SpecialEdDialog;
